import { Link } from 'react-router-dom';

import { useGreenCoffeeCounts } from '@/hooks/useGreenCoffee';
import { useIsAdmin } from '@/hooks/useIsAdmin';

import { SproutIcon } from './greenCoffeeIcons';

interface AdminToolRowProps {
  icon: React.ReactNode;
  label: string;
  subtitle: string;
  testId: string;
  to: string;
  badge?: number;
}

function AdminToolRow({ icon, label, subtitle, testId, to, badge }: AdminToolRowProps) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="flex items-center gap-3.5 px-4 py-3.5"
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-wolisho-brown/[0.06] text-wolisho-brown">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-black">{label}</p>
        <p className="mt-0.5 text-xs text-black/40">{subtitle}</p>
      </div>
      {badge != null && badge > 0 && (
        <span
          data-testid={`${testId}-badge`}
          className="inline-flex h-[22px] min-w-[22px] shrink-0 items-center justify-center rounded-full bg-wolisho-red px-[7px] text-xs font-bold tabular-nums text-white"
        >
          {String(badge)}
        </span>
      )}
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-black/25"
        aria-hidden="true"
      >
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </Link>
  );
}

// Staff-only tools section (design: Admin Navigation — Option B).
// Sits between the profile card and My Shelf; the heading mirrors the
// My Shelf style exactly. Visibility is cosmetic — the API's admin
// middleware is the real gate. Beta users links to its page (issue
// #240); Analytics still leads to the /admin placeholder.
export function AdminSection() {
  const isAdmin = useIsAdmin();
  const { counts } = useGreenCoffeeCounts(isAdmin);
  if (!isAdmin) return null;

  return (
    <section data-testid="admin-section">
      <h2 className="text-xl font-bold text-black mb-3">Admin</h2>
      <div className="bg-wolisho-card rounded-xl overflow-hidden mb-8">
        <AdminToolRow
          testId="admin-beta-users"
          to="/admin/beta-users"
          label="Beta users"
          subtitle="Accounts and roles"
          icon={
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 00-3-3.87" />
              <path d="M16 3.13a4 4 0 010 7.75" />
            </svg>
          }
        />
        <div className="mx-4 h-px bg-wolisho-brown/[0.06]" />
        <AdminToolRow
          testId="admin-roasters"
          to="/admin/roasters"
          label="Roaster inventory"
          subtitle="Shop URLs and platforms"
          icon={
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 8h1a4 4 0 010 8h-1" />
              <path d="M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z" />
              <line x1="6" y1="1" x2="6" y2="4" />
              <line x1="10" y1="1" x2="10" y2="4" />
              <line x1="14" y1="1" x2="14" y2="4" />
            </svg>
          }
        />
        <div className="mx-4 h-px bg-wolisho-brown/[0.06]" />
        <AdminToolRow
          testId="admin-request"
          to="/admin/request"
          label="Requests"
          subtitle="Coffees & roasters people asked us to add"
          icon={
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
              <path d="M5.45 5.11 2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" />
            </svg>
          }
        />
        <div className="mx-4 h-px bg-wolisho-brown/[0.06]" />
        <AdminToolRow
          testId="admin-crawl-runs"
          to="/admin/crawl-runs"
          label="Crawl runs"
          subtitle="Nightly + manual crawler health"
          icon={
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 12a9 9 0 11-3-6.7" />
              <polyline points="21 3 21 8 16 8" />
            </svg>
          }
        />
        <div className="mx-4 h-px bg-wolisho-brown/[0.06]" />
        <AdminToolRow
          testId="admin-green-coffee"
          to="/admin/green-coffee"
          label="Green coffee"
          subtitle="Grade coffees waiting to be published"
          badge={counts?.pending}
          icon={<SproutIcon size={18} />}
        />
        <div className="mx-4 h-px bg-wolisho-brown/[0.06]" />
        <AdminToolRow
          testId="admin-analytics"
          to="/admin"
          label="Analytics"
          subtitle="Check-ins, growth, engagement"
          icon={
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="12" y1="20" x2="12" y2="10" />
              <line x1="18" y1="20" x2="18" y2="4" />
              <line x1="6" y1="20" x2="6" y2="16" />
            </svg>
          }
        />
      </div>
    </section>
  );
}
